export type NumberQuestionId =
  | 'draft_m'
  | 'beam_m'
  | 'guest_cabins'
  | 'crew_count'
  | 'estimated_value'
  | 'year_built'
  | 'length_overall';

export type TextQuestionId =
  | 'home_port'
  | 'engines'
  | 'make'
  | 'model'
  | 'registration_number'
  | 'description';

export type CountryQuestionId = 'country_built' | 'flag_state';

export type SingleSelectQuestionId = 'usage_type';

export type MultiSelectQuestionId = 'cruising_areas' | 'vessel_type' | 'hull_material';

export type QuizQuestionId =
  | NumberQuestionId
  | TextQuestionId
  | CountryQuestionId
  | SingleSelectQuestionId
  | MultiSelectQuestionId;

type NumberQuestion = {
  id: NumberQuestionId;
  label: string;
  type: 'number';
};

type TextQuestion = {
  id: TextQuestionId;
  label: string;
  type: 'text';
};

type CountryQuestion = {
  id: CountryQuestionId;
  label: string;
  type: 'country';
};

type SingleSelectQuestion = {
  id: SingleSelectQuestionId;
  label: string;
  type: 'single_select';
  options: string[];
};

type MultiSelectQuestion = {
  id: MultiSelectQuestionId;
  label: string;
  type: 'multi_select';
  options: string[];
};

export type QuizQuestion =
  | NumberQuestion
  | TextQuestion
  | CountryQuestion
  | SingleSelectQuestion
  | MultiSelectQuestion;

export type QuizAnswers = {
  make?: string;
  model?: string;
  year_built?: number;
  length_overall?: number;
  registration_number?: string;
  hull_id?: string;
  description?: string;
  draft_m?: number;
  beam_m?: number;
  guest_cabins?: number;
  crew_count?: number;
  country_built?: string;
  home_port?: string;
  engines?: string;
  notes?: string;
  cruising_areas: string[];
  usage_type?: string;
  flag_state?: string;
  vessel_type: string[];
  hull_material: string[];
  estimated_value?: number;
};

export const CRUISING_LOCATION_OPTIONS = [
  'USA East Coast',
  'USA West Coast',
  'Bahamas & Caribbean',
];

export const USAGE_TYPE_OPTIONS = ['Private', 'Charter', 'Both'];

export const VESSEL_TYPE_OPTIONS = [
  'Motoryacht',
  'Sailing yacht',
  'Center Console',
  'Day Boat',
  'Trawler',
  'Sportfish Yacht',
  'Sailboat',
  'Powerboat',
  'Cruiser',
  'Commercial Vessel',
];

export const HULL_MATERIAL_OPTIONS = ['Fiberglass', 'Aluminum', 'Steel', 'Wood'];

export const questions: QuizQuestion[] = [
  {
    id: 'registration_number',
    label: 'Registration Number',
    type: 'text',
  },
  {
    id: 'flag_state',
    label: 'Flag State / Registration',
    type: 'country',
  },
  {
    id: 'make',
    label: 'Make',
    type: 'text',
  },
  {
    id: 'model',
    label: 'Model',
    type: 'text',
  },
  {
    id: 'year_built',
    label: 'Year Built',
    type: 'number',
  },
  {
    id: 'length_overall',
    label: 'Length Overall',
    type: 'number',
  },
  {
    id: 'draft_m',
    label: 'Draft',
    type: 'number',
  },
  {
    id: 'beam_m',
    label: 'Beam',
    type: 'number',
  },
  {
    id: 'guest_cabins',
    label: 'Number of Guest Cabins',
    type: 'number',
  },
  {
    id: 'crew_count',
    label: 'Number of Crew',
    type: 'number',
  },
  {
    id: 'country_built',
    label: 'Country Vessel Built In',
    type: 'country',
  },
  {
    id: 'home_port',
    label: 'Home Port',
    type: 'text',
  },
  {
    id: 'cruising_areas',
    label: 'Cruising Locations',
    type: 'multi_select',
    options: CRUISING_LOCATION_OPTIONS,
  },
  {
    id: 'usage_type',
    label: 'Vessel Use',
    type: 'single_select',
    options: USAGE_TYPE_OPTIONS,
  },
  {
    id: 'vessel_type',
    label: 'Vessel Type',
    type: 'multi_select',
    options: VESSEL_TYPE_OPTIONS,
  },
  {
    id: 'hull_material',
    label: 'Hull Material',
    type: 'multi_select',
    options: HULL_MATERIAL_OPTIONS,
  },
  {
    id: 'estimated_value',
    label: 'Estimated Value',
    type: 'number',
  },
  {
    id: 'engines',
    label: 'Engines',
    type: 'text',
  },
  {
    id: 'description',
    label: 'Description',
    type: 'text',
  },
];

export const getQuizAnswersCount = (answers: QuizAnswers) =>
  questions.reduce((count, question) => {
    const answer = answers[question.id];
    const isAnswered = Array.isArray(answer)
      ? answer.length > 0
      : answer !== undefined && answer !== '';
    return count + Number(isAnswered);
  }, 0);

export const QUIZ_QUESTIONS_COUNT = questions.length;
